import { DateFormatStyle } from "../types/api-types";
import { IHealth } from "./health_status";
import { PresenceType } from "./presence";

export type UserRoles =
  | "owner"
  | "member"
  | "scheduler"
  | "resource_manager"
  | "team_manager"
  | "page_manager"
  | "billing_manager"
  | "report_manager"
  | "visitor_manager"
  | "root"
  | "guest"
  | "bot_station"
  | "bot_room_station"
  | "bot_wayfinder_station"
  | "bot_basic"
  | "basic"
  | "deleted"
  // Placeholder identity a walk-up check-in is stamped with when no real
  // person is attached — never a real account.
  | "walkup";

/** Roles of the accounts station devices sign in with. */
const STATION_BOT_ROLES: UserRoles[] = [
  "bot_station",
  "bot_room_station",
  "bot_wayfinder_station"
];

export const isStationBotRole = (role: UserRoles): boolean =>
  STATION_BOT_ROLES.includes(role);

export const UserRolesLabels: {
  [idx in UserRoles]?: { label: string; description: string };
} = {
  owner: { label: "Admin", description: "" },
  member: { label: "Member", description: "" },
  scheduler: {
    label: "Scheduler",
    description: "Enables the user to create reservations on behalf of others"
  },
  resource_manager: {
    label: "Resource Manager",
    description:
      "Enable the user to manage workspaces in specific offices, floors, or zones"
  },
  team_manager: {
    label: "Team Manager",
    description: "Enable the user to manage other teams"
  },
  page_manager: {
    label: "Page Manager",
    description: "Enable the user to manage pages"
  },
  billing_manager: {
    label: "Billing Manager",
    description: "Enables the user to access billing and payment settings"
  },
  report_manager: {
    label: "Report Manager",
    description: "Enables the user to access reports"
  },
  visitor_manager: {
    label: "Visitor Manager",
    description: "Enable the user to manage visitor stations"
  }
};

/** Minimal reference to a resource dedicated to a user. Kept local to
 *  avoid a user ↔ resource entity import cycle. */
export interface IDedicatedResourceRef {
  id: string;
  name: string;
  label?: string | null;
  office_id?: string;
  parent_id?: string | null;
  type?: string;
  /**
   * The first day the desk was the user's, on the office's own calendar, as
   * `yyyy-MM-dd`. A day rather than a timestamp, so that reading it never
   * needs a zone and two offices can never disagree about which day it was.
   */
  dedicated_since?: string;
}

export interface IUserFull {
  id: string;
  email: string;
  name: string;
  time_zone: string;
  phone: string;
  location: string | null | undefined;
  birthdate: string;
  about: string;
  default_organization_id: null | string;
  profile_photo_url: null | string;
  is_being_impersonated: boolean;
  impersonated_by: null | string;
  just_created: boolean;
  permissions: string[];
  organization_user: null | string;
  can_respond_to_schedule: boolean;
  can_schedule_recurring: boolean;
  can_schedule_recurring_enabled: boolean;
  can_schedule_overlapping: boolean;
  can_schedule_overlapping_enabled: boolean;
  default_office_id: string;
  assigned_office_id: string;
  default_presence_type: PresenceType;
  is_in_grace_period_for_questionnaire: boolean;
  is_locked_by_questionnaire: boolean;
  last_accessed_desk_resource_id: string;
  last_accessed_parking_resource_id: null | string;
  last_accessed_meeting_room_resource_id: string;
  last_questionnaire_created: string;
  last_questionnaire_id: string;
  last_questionnaire_is_passed: boolean;
  minutes_left_grace_period_for_questionnaire: number;
  minutes_left_locked_by_questionnaire: number;
  organization_id?: string;
  roles: UserRoles[];
  status: "active" | "inactive";
  title: string;
  health: IHealth;
  health_is_manager: boolean;
  last_team_group_id: string | null;
  date_format: DateFormatStyle;
  is_obfuscated?: boolean;
  default_area_id?: string;
  /**
   * Employment details synced from the organization's HR directory (SailPoint).
   * All optional: the API omits them when the organization does not sync them
   * or the value is empty.
   */
  employee_type?: string;
  /** Email of this user's manager. Resolve it to a user to display them. */
  manager_email?: string;
  employee_number?: string;
  department_name?: string;
  cost_center_name?: string;
  /** ISO date, e.g. `2024-01-15`. */
  hire_date?: string;
  /** Desks dedicated to this user (owner of a dedicated resource). */
  dedicated_resources?: IDedicatedResourceRef[];
}

export type IUserFit = Pick<
  IUserFull,
  "id" | "profile_photo_url" | "name" | "roles"
>;
export type IUserFitSimple = Pick<
  IUserFull,
  "id" | "profile_photo_url" | "name"
>;

/**
 * The subset of user fields the user-picker components (UserSelectInput,
 * member access cards) actually read. Lets callers pass either a full
 * IUserFull or a lighter API user object — e.g. the users now embedded in
 * workplace-request category/type responses — without an adapter.
 */
export type IUserSelectable = Pick<
  IUserFull,
  "id" | "name" | "email" | "title" | "profile_photo_url"
>;
